import { EPaneId } from '../sheet-layout';

/** The default layer every DXF reader expects; nothing is drawn on it. */
export const DXF_DEFAULT_LAYER: string = '0';

/** The linetypes of the file, by their LTYPE names. */
export enum EDxfLinetype {
    Continuous = 'Continuous',
    /** The hidden line of ГОСТ 2.303: dash 4 mm, gap 1.5 mm on paper. */
    Dashed = 'DASHED',
    /** The thin dash-dot of ГОСТ 2.303 — axes and the walking line: dash 12 mm, gaps 3 mm, a dot. */
    DashDot = 'DASHDOT',
}

/**
 * What a line is on the drawing (ГОСТ 2.303), which is what a layer of a view holds. A view has
 * one layer per kind it uses: `<view>_Contour`, `<view>_Hidden` and so on.
 */
export enum EDxfLayerKind {
    /** The visible outline: the solid main line. */
    Contour = 'Contour',
    /** A thin solid line: secondary edges, construction lines, symbols. */
    Thin = 'Thin',
    /** An invisible outline: housed treads, the opening above. */
    Hidden = 'Hidden',
    /** An axis or the walking line. */
    Axis = 'Axis',
    /** A dimension: its line, extension lines, slashes and number. */
    Dim = 'Dim',
    /** A label and the view title. */
    Text = 'Text',
    /** A hatch or a solid fill. */
    Hatch = 'Hatch',
}

/** The layers of the sheet itself, in paper space. */
export enum EDxfSheetLayer {
    /** The inner frame and the main lines of the title block. */
    Frame = 'Sheet_Frame',
    /** The trimmed sheet edge and the thin rules of the title block. */
    Thin = 'Sheet_Thin',
    /** The title block text. */
    Text = 'Sheet_Text',
    /**
     * The viewports. The layer is off and never plotted: the sheet frames nothing but itself, and
     * a viewer that honours the layer state draws the views without a box around each. Off, not
     * frozen — AutoCAD hides a frozen viewport's contents too.
     */
    Viewports = 'Sheet_Viewports',
}

/** One row of the LAYER table. */
export interface IDxfLayer {
    name: string;
    /** AutoCAD colour index. */
    color: number;
    linetype: EDxfLinetype;
    /** Lineweight in hundredths of a millimetre (group 370). */
    lineweight: number;
    /** Whether the layer plots (group 290). */
    plot: boolean;
    /** Whether the layer is on; an off layer is written with a negative colour (group 62). */
    on: boolean;
}

type IDxfLayerStyle = Omit<IDxfLayer, 'name' | 'on'>;

/** The one layer the file writes switched off: the viewports'. */
const OFF_LAYERS: ReadonlySet<string> = new Set<string>([EDxfSheetLayer.Viewports]);

/** ГОСТ 2.303 with the main line s = 0.5 mm: thin lines 0.25, dimensions and hatching 0.18. */
const KIND_STYLES: Record<EDxfLayerKind, IDxfLayerStyle> = {
    [EDxfLayerKind.Contour]: { color: 7, linetype: EDxfLinetype.Continuous, lineweight: 50, plot: true },
    [EDxfLayerKind.Thin]: { color: 8, linetype: EDxfLinetype.Continuous, lineweight: 25, plot: true },
    [EDxfLayerKind.Hidden]: { color: 5, linetype: EDxfLinetype.Dashed, lineweight: 25, plot: true },
    [EDxfLayerKind.Axis]: { color: 1, linetype: EDxfLinetype.DashDot, lineweight: 25, plot: true },
    [EDxfLayerKind.Dim]: { color: 3, linetype: EDxfLinetype.Continuous, lineweight: 18, plot: true },
    [EDxfLayerKind.Text]: { color: 7, linetype: EDxfLinetype.Continuous, lineweight: 25, plot: true },
    [EDxfLayerKind.Hatch]: { color: 8, linetype: EDxfLinetype.Continuous, lineweight: 18, plot: true },
};

const SHEET_STYLES: Record<EDxfSheetLayer, IDxfLayerStyle> = {
    [EDxfSheetLayer.Frame]: { color: 7, linetype: EDxfLinetype.Continuous, lineweight: 50, plot: true },
    [EDxfSheetLayer.Thin]: { color: 7, linetype: EDxfLinetype.Continuous, lineweight: 25, plot: true },
    [EDxfSheetLayer.Text]: { color: 7, linetype: EDxfLinetype.Continuous, lineweight: 25, plot: true },
    [EDxfSheetLayer.Viewports]: { color: 9, linetype: EDxfLinetype.Continuous, lineweight: 25, plot: false },
};

const SHEET_LAYER_BY_NAME: ReadonlyMap<string, EDxfSheetLayer> = new Map<string, EDxfSheetLayer>(
    Object.values(EDxfSheetLayer).map((layer: EDxfSheetLayer): [string, EDxfSheetLayer] => [layer, layer])
);

/** The one layer a part file draws its outline on, in the main line. */
export const DXF_PART_LAYER: string = EDxfLayerKind.Contour;

/** The LAYER table of a part file: `0` and the outline layer. */
export function partLayers(): IDxfLayer[] {
    return [
        { name: DXF_DEFAULT_LAYER, color: 7, linetype: EDxfLinetype.Continuous, lineweight: 25, plot: true, on: true },
        { name: DXF_PART_LAYER, ...KIND_STYLES[EDxfLayerKind.Contour], on: true },
    ];
}

/** The order the kinds of one view take in the LAYER table. */
const KIND_ORDER: readonly EDxfLayerKind[] = Object.values(EDxfLayerKind);

/** The layer a view's lines of one kind go to: `<view>_<kind>`. */
export function paneLayerName(id: EPaneId, kind: EDxfLayerKind): string {
    return `${id}_${kind}`;
}

/** The style a layer name stands for: its kind's, its sheet layer's, or the default layer's. */
export function layerStyle(name: string): IDxfLayerStyle {
    const sheet: EDxfSheetLayer | undefined = SHEET_LAYER_BY_NAME.get(name);
    if (sheet) {
        return SHEET_STYLES[sheet];
    }
    const kind: EDxfLayerKind | undefined = KIND_ORDER.find((candidate: EDxfLayerKind): boolean => name.endsWith(`_${candidate}`));
    return kind ? KIND_STYLES[kind] : KIND_STYLES[EDxfLayerKind.Thin];
}

/**
 * The LAYER table of a file: `0`, then the layers each view uses in sheet order and kind order,
 * then the sheet layers. A view's kind no entity uses gets no layer.
 */
export function dxfLayers(paneIds: readonly EPaneId[], used: ReadonlySet<string>): IDxfLayer[] {
    const named: (name: string) => IDxfLayer = (name: string): IDxfLayer => ({ name, ...layerStyle(name), on: !OFF_LAYERS.has(name) });
    const paneLayers: IDxfLayer[] = paneIds.flatMap((id: EPaneId): IDxfLayer[] =>
        KIND_ORDER.map((kind: EDxfLayerKind): string => paneLayerName(id, kind))
            .filter((name: string): boolean => used.has(name))
            .map(named)
    );
    return [
        { name: DXF_DEFAULT_LAYER, color: 7, linetype: EDxfLinetype.Continuous, lineweight: 25, plot: true, on: true },
        ...paneLayers,
        ...Object.values(EDxfSheetLayer).map(named),
    ];
}
